import React from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { deleteTask, unassignTask } from 'actions/profile';

import { DeleteWarning, Help, Loading, Paging, SystemText } from '@salesperformanceint/e4shared';
import TaskIcon from 'views/user/task/components/taskIcon';
import Progress from '../progress';

import ManageTask from 'views/user/components/manageTask';
import { e4Format, addDays, isPast, isToday } from 'helpers/dates';
import { setShowSuccess } from 'actions/ui';

export const TaskList = ({
    reload,
    tasks,
    loading,
    recordCount,
    setPage,
    page,
    sortBy,
    setSortBy,
    descending,
    setDescending,
}) => {
    const dispatch = useDispatch();
    const [showManageTask, setShowManageTask] = React.useState(false);
    const [showProgress, setShowProgress] = React.useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = React.useState(false);
    const [showUnassignWarning, setShowUnassignWarning] = React.useState(false);
    const [deleting, setDeleting] = React.useState(null);
    const [unassigning, setUnassigning] = React.useState(null);
    const [task, setTask] = React.useState(null);

    const showDelete = id => {
        setDeleting(id);
        setShowDeleteWarning(true);
    };

    const showUnassign = id => {
        setUnassigning(id);
        setShowUnassignWarning(true);
    };

    const deleteTaskCall = confirm => {
        setDeleting(null);
        setShowDeleteWarning(false);
        if (confirm) {
            dispatch(
                deleteTask(deleting, () => {
                    reload?.();
                    dispatch(setShowSuccess(SystemText('ObjectiveTracker/Delete/Success')));
                })
            );
        }
    };

    const unassignTaskCall = confirm => {
        setUnassigning(null);
        setShowUnassignWarning(false);
        if (confirm) {
            dispatch(
                unassignTask(unassigning, () => {
                    reload?.();
                    dispatch(setShowSuccess(SystemText('ObjectiveTracker/Unassign/Success')));
                })
            );
        }
    };

    if (loading || !tasks) return <Loading />;

    const taskBadgeColour = task => {
        if (task.date_Completed) return '';
        if (task.outstanding_Users_Count === 0) return '';
        return isPast(task.due) && !isToday(task.due)
            ? 'label table-label label-danger'
            : isPast(addDays(task.due, -14))
              ? 'label table-label label-warning'
              : '';
    };

    const tableFooter = (
        <div className="text-center mt10">
            <Paging goToPage={page => setPage(page)} pageSize="20" recordCount={recordCount || 0} page={page} />
        </div>
    );

    const ensureArray = task => {
        task.overdue_Users = task.overdue_Users ? task.overdue_Users : [];
        task.outstanding_Users = task.outstanding_Users ? task.outstanding_Users : [];
        task.awaiting_Users = task.awaiting_Users ? task.awaiting_Users : [];
        task.completed_Users = task.completed_Users ? task.completed_Users : [];
    };

    const text = {
        table: {
            objective: SystemText('ObjectiveTracker/Table/Objective'),
            owner: SystemText('ObjectiveTracker/Table/Owner'),
            due: SystemText('ObjectiveTracker/Table/Due'),
            assigned: SystemText('ObjectiveTracker/Table/Assigned'),
            overdue: SystemText('ObjectiveTracker/Table/Overdue'),
            outstanding: SystemText('ObjectiveTracker/Table/Outstanding'),
            awaiting: SystemText('ObjectiveTracker/Table/Awaiting'),
            complete: SystemText('ObjectiveTracker/Table/Complete'),
        },
        noResults: SystemText('ObjectiveTracker/NoResults'),
        various: SystemText('ObjectiveTracker/Various'),
        edit: SystemText('ObjectiveTracker/Edit'),
        unassign: {
            none: SystemText('ObjectiveTracker/Unassign/None'),
            all: SystemText('ObjectiveTracker/Unassign/All'),
            warning: SystemText('ObjectiveTracker/Unassign/Warning'),
        },
        delete: {
            delete: SystemText('ObjectiveTracker/Delete'),
            cannot: SystemText('ObjectiveTracker/Delete/Cannot'),
            warning: SystemText('ObjectiveTracker/Delete/Warning'),
        },
    };

    const ordering = ref => {
        return ref === sortBy ? (
            <span className="ml5">
                <i className={`fa fa-caret-${descending ? 'down' : 'up'}`} />
            </span>
        ) : null;
    };

    const th = (label, ref, alignRight) => {
        return (
            <th className={alignRight ? 'text-right' : ''}>
                <span
                    className="pointer bold"
                    onClick={() => {
                        setDescending(ref === sortBy ? !descending : false);
                        setSortBy(ref);
                    }}
                >
                    {label}
                    {ordering(ref)}
                </span>
            </th>
        );
    };

    return (
        <div>
            <div className="table-responsive">
                <table className="table table-hover mb10">
                    <thead>
                        <tr>
                            {th(text.table.objective, 'name')}
                            {th(text.table.owner, 'owner')}
                            {th(text.table.assigned, 'assigned')}
                            {th(text.table.due, 'due')}
                            {th(text.table.overdue, 'overdue', true)}
                            {th(text.table.outstanding, 'outstanding', true)}
                            {th(text.table.awaiting, 'awaiting', true)}
                            {th(text.table.complete, 'complete', true)}
                            <th className="text-right" />
                        </tr>
                    </thead>
                    <tbody>
                        {tasks.length === 0 && (
                            <tr>
                                <td colSpan={9} className="td-no-results">
                                    {text.noResults}
                                </td>
                            </tr>
                        )}
                        {tasks.map((task, i) => {
                            ensureArray(task);
                            const overdueClass = task.overdue_Users_Count === 0 ? '' : 'pointer label label-danger';
                            const outstandingClass = task.outstanding_Users_Count === 0 ? '' : 'pointer label label-info';
                            const awaitingClass = task.awaiting_Users_Count === 0 ? '' : 'pointer label label-warning';
                            const completedClass = task.completed_Users_Count === 0 ? '' : 'pointer label label-success';
                            const onProgressClick = () => {
                                setTask(task);
                                setShowProgress(true);
                            };

                            return (
                                <tr key={`${task.task_Id}`} data-task-id={task.task_Id}>
                                    <td onClick={onProgressClick} className="pointer">
                                        {task.name}
                                        <TaskIcon icons={task.icons} />
                                        {task.course && <Help id={`course-help-${task.task_Id}`}>{task.course}</Help>}
                                    </td>

                                    <td onClick={onProgressClick} className="pointer no-wrap">
                                        {task.owner_Fullname}
                                    </td>
                                    <td onClick={onProgressClick} className="pointer no-wrap">
                                        {task.otherAssignedDates ? (
                                            <span>{text.various}</span>
                                        ) : (
                                            <span>{e4Format(task.date_Assigned)}</span>
                                        )}
                                    </td>
                                    <td onClick={onProgressClick} className="pointer no-wrap">
                                        <span className={taskBadgeColour(task)}>{e4Format(task.due)}</span>
                                    </td>
                                    <td onClick={onProgressClick} className="pointer">
                                        <span className={`${overdueClass} pull-right`}>{task.overdue_Users_Count}</span>
                                    </td>
                                    <td onClick={onProgressClick} className="pointer">
                                        <span className={`${outstandingClass} pull-right`}>
                                            {task.outstanding_Users_Count}
                                        </span>
                                    </td>
                                    <td onClick={onProgressClick} className="pointer">
                                        <span className={`${awaitingClass} pull-right`}>{task.awaiting_Users_Count}</span>
                                    </td>
                                    <td onClick={onProgressClick} className="pointer">
                                        <span className={`${completedClass} pull-right`}>{task.completed_Users_Count}</span>
                                    </td>
                                    <td className="text-right table-btns">
                                        <button
                                            title={text.edit}
                                            className="btn btn-primary btn-sm"
                                            onClick={() => {
                                                setTask(task);
                                                setShowManageTask(true);
                                            }}
                                        >
                                            <i className="fa fa-edit" />
                                        </button>
                                        <button
                                            title={
                                                task.outstanding_Users_Count + task.awaiting_Users_Count === 0
                                                    ? text.unassign.none
                                                    : text.unassign.all
                                            }
                                            onClick={() => showUnassign(task.task_Id)}
                                            disabled={task.outstanding_Users_Count + task.awaiting_Users_Count === 0}
                                            className="btn btn-warning btn-sm"
                                        >
                                            <i className="fa fa-undo" />
                                        </button>
                                        <button
                                            title={
                                                task.completed_Users_Count + task.awaiting_Users_Count > 0
                                                    ? text.delete.cannot
                                                    : text.delete.delete
                                            }
                                            onClick={() => showDelete(task.task_Id)}
                                            disabled={task.completed_Users_Count + task.awaiting_Users_Count > 0}
                                            className="btn btn-danger btn-sm"
                                        >
                                            <i className="fa fa-trash" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {tableFooter}
            </div>
            <ManageTask
                show={showManageTask}
                values={{ ...task }}
                reload={() => reload?.()}
                close={() => setShowManageTask(false)}
            />
            <Progress task={task} show={showProgress} close={() => setShowProgress(false)} />
            <DeleteWarning
                message={text.delete.warning}
                show={showDeleteWarning}
                close={confirm => deleteTaskCall(confirm)}
            />
            <DeleteWarning
                message={text.unassign.warning}
                show={showUnassignWarning}
                close={confirm => unassignTaskCall(confirm)}
            />
        </div>
    );
};

export default withRouter(TaskList);
